/**
 * =========================================================
 * Social Media Hub - Modern Typography
 * =========================================================
 *
 * Clean, modern typography with Inter font family for a
 * polished, professional appearance.
 */

// Material Dashboard 2 React Base Styles
import colors from "assets/theme/base/colors";

// Material Dashboard 2 React Helper Functions
import pxToRem from "assets/theme/functions/pxToRem";

const { dark } = colors;

const baseProperties = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeightLighter: 200,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  fontSizeXXS: pxToRem(10),
  fontSizeXS: pxToRem(12),
  fontSizeSM: pxToRem(14),
  fontSizeMD: pxToRem(16),
  fontSizeLG: pxToRem(18),
  fontSizeXL: pxToRem(20),
  fontSize2XL: pxToRem(24),
  fontSize3XL: pxToRem(30),
  fontSize4XL: pxToRem(36),
  fontSize5XL: pxToRem(48),
};

const baseHeadingProperties = {
  fontFamily: baseProperties.fontFamily,
  color: dark.main,
  fontWeight: baseProperties.fontWeightSemiBold,
  letterSpacing: "-0.02em",
};

const baseDisplayProperties = {
  fontFamily: baseProperties.fontFamily,
  color: dark.main,
  fontWeight: baseProperties.fontWeightBold,
  lineHeight: 1.1,
  letterSpacing: "-0.03em",
};

const typography = {
  fontFamily: baseProperties.fontFamily,
  fontWeightLighter: baseProperties.fontWeightLighter,
  fontWeightLight: baseProperties.fontWeightLight,
  fontWeightRegular: baseProperties.fontWeightRegular,
  fontWeightMedium: baseProperties.fontWeightMedium,
  fontWeightSemiBold: baseProperties.fontWeightSemiBold,
  fontWeightBold: baseProperties.fontWeightBold,

  h1: {
    fontSize: pxToRem(40),
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
    ...baseHeadingProperties,
  },

  h2: {
    fontSize: pxToRem(32),
    lineHeight: 1.25,
    letterSpacing: "-0.02em",
    ...baseHeadingProperties,
  },

  h3: {
    fontSize: pxToRem(26),
    lineHeight: 1.3,
    letterSpacing: "-0.015em",
    ...baseHeadingProperties,
  },

  h4: {
    fontSize: pxToRem(22),
    lineHeight: 1.35,
    letterSpacing: "-0.01em",
    ...baseHeadingProperties,
  },

  h5: {
    fontSize: pxToRem(18),
    lineHeight: 1.4,
    letterSpacing: "-0.01em",
    ...baseHeadingProperties,
  },

  h6: {
    fontSize: pxToRem(16),
    lineHeight: 1.5,
    fontWeight: baseProperties.fontWeightMedium,
    letterSpacing: "-0.005em",
    fontFamily: baseProperties.fontFamily,
    color: dark.main,
  },

  subtitle1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeLG,
    fontWeight: baseProperties.fontWeightMedium,
    lineHeight: 1.5,
    letterSpacing: "-0.01em",
  },

  subtitle2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightMedium,
    lineHeight: 1.5,
    letterSpacing: "-0.005em",
  },

  body1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.6,
  },

  body2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.6,
  },

  button: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: baseProperties.fontWeightSemiBold,
    lineHeight: 1.5,
    textTransform: "none",
    letterSpacing: "0.01em",
  },

  caption: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXS,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.4,
  },

  overline: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXS,
    fontWeight: baseProperties.fontWeightSemiBold,
    lineHeight: 1.5,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },

  // Display sizes for large headings
  d1: {
    fontSize: pxToRem(64),
    ...baseDisplayProperties,
  },

  d2: {
    fontSize: pxToRem(56),
    ...baseDisplayProperties,
  },

  d3: {
    fontSize: pxToRem(48),
    ...baseDisplayProperties,
  },

  d4: {
    fontSize: pxToRem(40),
    ...baseDisplayProperties,
  },

  d5: {
    fontSize: pxToRem(32),
    ...baseDisplayProperties,
  },

  d6: {
    fontSize: pxToRem(24),
    ...baseDisplayProperties,
  },

  // Size utilities
  size: {
    xxs: baseProperties.fontSizeXXS,
    xs: baseProperties.fontSizeXS,
    sm: baseProperties.fontSizeSM,
    md: baseProperties.fontSizeMD,
    lg: baseProperties.fontSizeLG,
    xl: baseProperties.fontSizeXL,
    "2xl": baseProperties.fontSize2XL,
    "3xl": baseProperties.fontSize3XL,
    "4xl": baseProperties.fontSize4XL,
    "5xl": baseProperties.fontSize5XL,
  },

  // Line height utilities
  lineHeight: {
    xs: 1.1,
    sm: 1.25,
    md: 1.5,
    lg: 1.75,
    xl: 2,
  },
};

export default typography;
